import React from 'react';
import Chip from '@mui/material/Chip';

import { BugSeverity } from '../types/BugSeverity';
import { BugStatus } from '../types/BugStatus';
import { colors } from '../theme/tokens';

type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'dark';

/* Soft tinted chips; solid red is reserved for unread counts (CountChip unread). */
const tones: Record<Tone, { bg: string; fg: string }> = {
    neutral: { bg: '#e9edf3', fg: '#445066' },
    primary: { bg: colors.primaryTint, fg: colors.primary },
    success: { bg: '#e7f6ea', fg: '#2e7d32' },
    warning: { bg: '#fff4e5', fg: '#b26a00' },
    danger : { bg: '#fdecea', fg: '#c62828' },
    dark   : { bg: '#e3e6ec', fg: '#263238' },
};

export const ToneChip: React.FC<{ label: React.ReactNode; tone?: Tone; title?: string }> = ({ label, tone = 'neutral', title }) => (
    <Chip
        size="small"
        label={label}
        title={title}
        sx={{ backgroundColor: tones[tone].bg, color: tones[tone].fg, fontWeight: 500, fontSize: 12, height: 22, borderRadius: '11px' }}
    />
);

const severityTone: Record<string, Tone> = {
    [BugSeverity.Low]     : 'neutral',
    [BugSeverity.Medium]  : 'warning',
    [BugSeverity.High]    : 'danger',
    [BugSeverity.Critical]: 'dark',
};

export const SeverityChip: React.FC<{ severity?: string }> = ({ severity }) => (
    <ToneChip label={severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'N/A'} tone={severityTone[severity || ''] || 'neutral'} />
);

/* Same wording as the customer's bug table */
export const bugStatusLabel = (status: string): string => {
    switch (status) {
        case BugStatus.New       : return 'Open';
        case BugStatus.Fixed     : return 'Fixed';
        case BugStatus.NotABug   : return 'Not a bug';
        case BugStatus.Duplicated: return 'Duplicated';
        case BugStatus.Deferred  : return 'Closed';
        default                  : return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
    }
};

const statusTone: Record<string, Tone> = {
    [BugStatus.New]       : 'primary',
    [BugStatus.Fixed]     : 'success',
    [BugStatus.NotABug]   : 'warning',
    [BugStatus.Duplicated]: 'neutral',
    [BugStatus.Deferred]  : 'neutral',
};

export const BugStatusChip: React.FC<{ status: string }> = ({ status }) => (
    <ToneChip label={bugStatusLabel(status)} tone={statusTone[status] || 'neutral'} />
);

/* Grey count, or solid red when it counts unread items */
export const CountChip: React.FC<{ value: React.ReactNode; unread?: boolean; title?: string }> = ({ value, unread = false, title }) => (
    <Chip
        size="small"
        label={value}
        title={title}
        sx={{
            ml: 0.75,
            height: 20,
            fontSize: 12,
            fontWeight: 500,
            backgroundColor: unread ? '#d32f2f' : '#e9edf3',
            color: unread ? '#fff' : '#445066',
        }}
    />
);
